import {FC, useMemo} from 'react';
import {Box, SvgIcon, Typography} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import {BusinessOutlined, Logout, MapsHomeWorkSharp, SvgIconComponent} from '@mui/icons-material';
import {FormattedMessage} from 'react-intl';
import {Link, Navigate} from 'react-router-dom';
import {useAppSelector, useBusinessId} from 'src/app/hooks';
import {ContractStatusEnum} from 'src/enums';
import {useLogout} from 'src/hooks/useLogout';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {useCan, useCanOpen, usePersona, useScope} from 'src/permissions/hooks';
import {PAGE_PERMISSION} from 'src/permissions/pages';
import {NotificationMarker} from 'src/components/common/NotificationMarker';
import {
    AssistantIcon,
    ChargersIcon,
    ContractsIcon,
    DashboardIcon,
    EmployeesIcon,
    InvoicesIcon,
    LocationsIcon,
    NotificationsIcon,
    ReportsIcon,
    TagsIcon,
    TariffsIcon,
    TechnicalTeamIcon,
    UsersIcon,
} from 'src/components/icons/ToolbarIcons';
import {AuthContractDTO, AuthUserDTO} from 'src/types';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {getEvDriverNameFromAuth} from 'src/utils/userNameFormatter';
import {
    avatarSx,
    identityBusinessSx,
    identityNameSx,
    identitySx,
    identityTextSx,
    moreScreenSx,
    tileBadgeSx,
    tileGridSx,
    tileSx,
} from './MoreMenu.sx';

interface MoreTile {
    key: string;
    show: boolean;
    to: string;
    icon: SvgIconComponent;
    viewBox: string;
    /** A catalog message id, for every entry whose label the drawer already translates. */
    labelId?: string;
    /** The literal the drawer renders for the one entry that has never had a key. */
    label?: string;
    badge?: boolean;
}

/**
 * Everything the bottom bar cannot hold, as one screen.
 *
 * The same entries the drawer's {@link MainNavigation} would show, asked in the same order with the
 * same questions — a tile is a door to a page, so it must open exactly when the drawer's link does.
 * Below `md` only: a desktop has the drawer standing open and no use for this, so the route sends it
 * to the locations list rather than drawing a phone menu on a wide screen.
 */
export const MoreMenu: FC = () => {
    const {isDesktop} = useScreenTier();
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    const businessId = useBusinessId();
    const {authInfo} = useAppSelector((state) => state.common);
    const persona = usePersona();
    const handleLogOut = useLogout();
    const canReadBusiness = useCan('businesses.read');
    // Reading one charger, as opposed to listing them: what a driver holds for the charger that is
    // theirs.
    const canReadChargers = useCan('chargers.read');
    // Which staff directory somebody gets follows their reach, not the kind of account they are.
    const companyReach = useScope()?.type === 'COMPANY';
    const showsBusinessDirectory = !companyReach;
    const can = {
        locations: useCanOpen(PAGE_PERMISSION[AppRouteEnum.locations]),
        chargers: useCanOpen(PAGE_PERMISSION[AppRouteEnum.chargers]),
        employees: useCanOpen(PAGE_PERMISSION[AppRouteEnum.employee]),
        users: useCanOpen(PAGE_PERMISSION[AppRouteEnum.users]),
        rfid: useCanOpen(PAGE_PERMISSION[AppRouteEnum.rfid]),
        contracts: useCanOpen(PAGE_PERMISSION[AppRouteEnum.contracts]),
        tariffs: useCanOpen(PAGE_PERMISSION[AppRouteEnum.tariffs]),
        reports: useCanOpen(PAGE_PERMISSION[AppRouteEnum.reports]),
        invoices: useCanOpen(PAGE_PERMISSION[AppRouteEnum.invoices]),
        technicalTeam: useCanOpen(PAGE_PERMISSION[AppRouteEnum.technicalTeam]),
        notifications: useCanOpen(PAGE_PERMISSION[AppRouteEnum.notifications]),
        reOwner: useCanOpen(PAGE_PERMISSION[AppRouteEnum.reOwner]),
        accessControl: useCanOpen(PAGE_PERMISSION[AppRouteEnum.accessControl]),
        businesses: useCanOpen(PAGE_PERMISSION[AppRouteEnum.businesses]),
        assistant: useCanOpen(PAGE_PERMISSION[AppRouteEnum.assistant]),
    };
    const availableContract = authInfo.contracts?.find(
        (contract: AuthContractDTO) =>
            contract.status === ContractStatusEnum.WAIT_FOR_SIGNATURE ||
            contract?.status === ContractStatusEnum.OPERATION ||
            contract?.status === ContractStatusEnum.INSTALLATION
    );
    const operationalContract = authInfo.contracts?.find(
        (contract: AuthContractDTO) => contract?.status === ContractStatusEnum.OPERATION
    );
    // The same formatter the sessions and transactions tables use, so one account reads the same
    // everywhere: not everyone has a first and last name, and concatenating the two fields directly
    // produced "null null" for exactly those accounts.
    const userName = useMemo(() => getEvDriverNameFromAuth(authInfo as AuthUserDTO), [authInfo]);
    const initials = useMemo(
        () =>
            String(userName ?? '')
                .trim()
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((word) => word[0]?.toUpperCase() ?? '')
                .join(''),
        [userName]
    );

    const tiles: MoreTile[] = [
        {
            key: 'assistant',
            show: can.assistant,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.assistant]),
            icon: AssistantIcon,
            viewBox: '0 0 20 20',
            label: 'Assistant',
        },
        {
            key: 'businesses',
            show: can.businesses,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.businesses]),
            icon: BusinessOutlined,
            viewBox: '0 0 24 24',
            labelId: 'businesses',
        },
        {
            // The permission and the business itself: the platform administrator holds
            // businesses.read while having none, and the permission alone would build a path from
            // `null.id`.
            key: 'dashboard',
            show: canReadBusiness && !!businessId,
            to: AppRouteSpace([
                AppRouteEnum.app,
                AppRouteEnum.users,
                AppRouteEnum.business,
                String(businessId),
            ]),
            icon: DashboardIcon,
            viewBox: '0 0 20 20',
            labelId: 'dashboard',
        },
        {
            key: 'locations',
            show: can.locations,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.locations]),
            icon: LocationsIcon,
            viewBox: '0 0 20 20',
            labelId: 'locations',
        },
        {
            key: 'chargers',
            show: can.chargers,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.chargers]),
            icon: ChargersIcon,
            viewBox: '0 0 20 20',
            labelId: 'chargers',
        },
        {
            // Your own charger, when you cannot open the list. Both at once would be two doors to
            // the same room.
            key: 'ownCharger',
            show: !can.chargers && canReadChargers && !!authInfo.chargePoint && !!availableContract,
            to: AppRouteSpace([
                AppRouteEnum.app,
                AppRouteEnum.chargers,
                String(authInfo.chargePoint?.id ?? ''),
            ]),
            icon: ChargersIcon,
            viewBox: '0 0 20 20',
            labelId: 'charger',
        },
        {
            // The contract flow, which redirects anyone who is neither a driver nor a company
            // straight to Locations - so the tile asks the same question the page asks about itself.
            key: 'welcome',
            show: (persona.isDriver || persona.isCompany) && !operationalContract,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.welcome]),
            icon: MapsHomeWorkSharp,
            viewBox: '0 0 20 20',
            labelId: 'home',
        },
        {
            key: 'ownContract',
            show: !can.contracts && !!availableContract,
            to: AppRouteSpace([
                AppRouteEnum.app,
                AppRouteEnum.contracts,
                String(operationalContract?.id ?? availableContract?.id ?? ''),
            ]),
            icon: ContractsIcon,
            viewBox: '0 0 20 20',
            labelId: 'contracts',
        },
        {
            // A company's own staff list. Permission and reach both have to agree: the first is
            // authorization, the second is whose menu it is.
            key: 'employees',
            show: companyReach && can.employees,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.employee]),
            icon: EmployeesIcon,
            viewBox: '0 0 20 20',
            labelId: 'employee',
        },
        {
            key: 'users',
            show: showsBusinessDirectory && can.users,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users]),
            icon: UsersIcon,
            viewBox: '0 0 20 20',
            labelId: 'users',
        },
        {
            key: 'rfid',
            show: can.rfid,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.rfid]),
            icon: TagsIcon,
            viewBox: '0 0 24 22',
            labelId: 'rfidsTabHeader',
        },
        {
            key: 'contracts',
            show: can.contracts,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.contracts]),
            icon: ContractsIcon,
            viewBox: '0 0 20 20',
            labelId: 'contracts',
        },
        {
            key: 'tariffs',
            show: can.tariffs,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.tariffs]),
            icon: TariffsIcon,
            viewBox: '0 0 20 20',
            labelId: 'tariffs',
        },
        {
            key: 'reports',
            show: can.reports,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.reports]),
            icon: ReportsIcon,
            viewBox: '0 0 20 20',
            labelId: 'statistics',
        },
        {
            key: 'invoices',
            show: can.invoices,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.invoices]),
            icon: InvoicesIcon,
            viewBox: '0 0 20 20',
            labelId: 'invoices',
        },
        {
            key: 'technicalTeam',
            show: showsBusinessDirectory && can.technicalTeam,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.technicalTeam]),
            icon: TechnicalTeamIcon,
            viewBox: '0 0 20 20',
            labelId: 'technical-team',
        },
        {
            key: 'notifications',
            show: can.notifications,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.notifications]),
            icon: NotificationsIcon,
            viewBox: '0 0 20 20',
            labelId: 'notifications',
            badge: true,
        },
        {
            key: 'accessControl',
            show: can.accessControl,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.accessControl]),
            icon: AdminPanelSettingsIcon,
            viewBox: '0 0 24 24',
            labelId: 'accessControl',
        },
        {
            key: 'reOwner',
            show: can.reOwner,
            to: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.reOwner]),
            icon: MapsHomeWorkSharp,
            viewBox: '0 0 20 20',
            labelId: 'reOwner',
        },
    ];

    if (isDesktop) {
        return <Navigate to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.locations])} replace/>;
    }

    return (
        <Box sx={moreScreenSx}>
            <Box sx={identitySx}>
                <Box sx={avatarSx}>
                    {authInfo.avatar ? (
                        <img
                            src={authInfo.avatar.url}
                            alt={userName}
                        />
                    ) : (
                        <Typography variant="caption">{initials}</Typography>
                    )}
                </Box>
                <Box sx={identityTextSx}>
                    <Typography sx={identityNameSx}>{userName}</Typography>
                    {authInfo.business?.name && (
                        <Typography sx={identityBusinessSx}>{authInfo.business.name}</Typography>
                    )}
                </Box>
            </Box>

            <Box sx={tileGridSx}>
                {tiles
                    .filter((tile) => tile.show)
                    .map(({key, to, icon, viewBox, labelId, label, badge}) => (
                        <Box
                            key={key}
                            component={Link}
                            to={to}
                            sx={tileSx()}
                        >
                            <SvgIcon
                                component={icon}
                                viewBox={viewBox}
                            />
                            <Typography>
                                {labelId ? <FormattedMessage id={labelId}/> : label}
                            </Typography>
                            {badge && (
                                <Box sx={tileBadgeSx}>
                                    <NotificationMarker/>
                                </Box>
                            )}
                        </Box>
                    ))}
                {/* The same logout the drawer performs, lifted into a hook rather than written
                    twice: clearing storage without ending the session on the server leaves the
                    refresh token valid. */}
                <Box
                    component="button"
                    type="button"
                    onClick={handleLogOut}
                    sx={tileSx(true)}
                >
                    <Logout/>
                    <Typography>
                        <FormattedMessage id="logOut"/>
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};
